import { Pagination, Table } from 'antd';
import { Open16Regular } from '@fluentui/react-icons';
import { ColumnsType } from 'antd/lib/table/interface';
import * as React from 'react';
import { IMemberStatementWork } from '../../../types/IMemberStatementSearchResult';
import IMembersPortalComponentDataItem from '../../../../redux/types/IMembersPortalComponentDataItem';
import { mp_addTab } from '../../../../redux/reducers/MembersPortalReducer';
import { ITabReduxItem } from '../../../../redux/types/ITabReduxItem';
import { MEMBER_STATEMENTS_USAGE_DETAILS_PAGE, PAGE_SIZE_OPTIONS } from '../../../Consts';
import IconTextButton from '../../../../repertoire/components/iconTextButton/IconTextButton';
import ErrorFallback from '../../../../telemetry/components/ErrorFallback';
import { MembersPortalService } from '../../../../redux/services/MembersPortalService';
import { IMemberStatementWorkState } from '../../../../redux/types/IMemberStatementState';
import { useServerPagination } from '../../hooks/useServerPagination';
import { formatCurrencyWithCommas } from '../../../../util/tools';

export interface IMemberStatementWorksGridProps {
    addTab: typeof mp_addTab;
    description: string;
    segment: string;
    userNameNumber: string;
    memberStatementWorkState: IMemberStatementWorkState;
    workDetailGridsComponentData: IMembersPortalComponentDataItem;
}

const DEFAULT_PAGE_SIZE = 500;

export const MemberStatementWorksGrid: React.FC<IMemberStatementWorksGridProps> = (props) => {
    const fetchPage = (token?: string, pageSizeOverride = DEFAULT_PAGE_SIZE) =>
        MembersPortalService.getStatementWorks(
            props.memberStatementWorkState.paymentRunID,
            props.userNameNumber,
            props.memberStatementWorkState.distributionCode,
            props.memberStatementWorkState.poolCode,
            props.memberStatementWorkState.segment,
            token,
            pageSizeOverride
        );

    const pagination = useServerPagination<IMemberStatementWork>({
        queryKey: [
            'member-statement-works',
            props.memberStatementWorkState?.refreshKey,
        ],
        fetchPage,
        pageSize: DEFAULT_PAGE_SIZE
    });

    const openUsageDetails = (work: IMemberStatementWork) => {
        const { addTab, memberStatementWorkState, description, segment } = props;
        const titleParts = [work.workNumber || work.workID, work.title].filter(Boolean);
        const title = titleParts.length > 0
            ? titleParts.join(' – ')
            : 'Work';
        const usageDetailsTab: ITabReduxItem = {
            title: title,
            tabTitle: title,
            component: MEMBER_STATEMENTS_USAGE_DETAILS_PAGE.component,
            memberStatementUsageDetailsState: {
                paymentRunCode: memberStatementWorkState.paymentRunCode,
                paymentRunID: work.paymentRunID,
                description,
                distributionCode: work.distributionCode,
                poolCode: work.poolCode,
                segment,
                workID: work.workID,
                workNumber: work.workNumber,
                amount: work.amount,
                statementLineKey: work.statementLineKey,
                refreshKey: Date.now() // forces refetch when opened
            }
        };

        addTab(usageDetailsTab);
    };

    const data: IMemberStatementWork[] = pagination.data.map(w => ({
        ...w,
        sharePercentage: parseFloat(w.sharePercentage).toFixed(2),
        amount: formatCurrencyWithCommas(w.amount, false, 2)
    }));

    const columns: ColumnsType<IMemberStatementWork> =
        props.workDetailGridsComponentData.fields.map((w) => {
            if (w.name.includes('open')) {
                return {
                    title: w.data.split('/').pop(),
                    dataIndex: w.name,
                    key: w.name,
                    fixed: 'right',
                    render: (_, work) => (
                        <IconTextButton
                            text={w.data.split('/').pop()}
                            icon={Open16Regular}
                            onClick={() => openUsageDetails(work)}
                        />
                    )
                };
            }

            return {
                title: w.data,
                dataIndex: w.name,
                key: w.name,
                sorter: ['workNumber', 'ipiNameNumber', 'ipName'].includes(w.name)
                    ? (a, b) => a[w.name]?.localeCompare(b[w.name])
                    : undefined
            };
        });

    if (!props.memberStatementWorkState){
        return null;
    }

    if (pagination.isError) {
        return <ErrorFallback />;
    }

    return (
        <>
            <div style={{ width: '100%', overflow: 'auto' }}>
                <Table
                    loading={pagination.isLoading}
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                />
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                    current={pagination.currentPage}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    showSizeChanger
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onChange={pagination.onPageChange}
                />
            </div>
        </>
    );
};
